import lodash from 'lodash';
import EpgEvent from '@shared/classes/epgEvent';
import Rail from '@shared/classes/rail';
import {
  RailDisplayMode,
  RailOrderSort,
  RailOrderType,
  RailType,
} from '@shared/types/enums/rail';

const EpgUtils = {
  buildRail(events: EpgEvent[], channelId: string) {
    if (events.length && events[0].channel?.id === channelId) {
      const baseRail = {
        section: {
          presentationType: RailDisplayMode.EPG,
          name: channelId,
        },
      };
      const rail = Rail.constructorWithData(
        baseRail,
        events,
        RailType.EPG,
        false,
        true
      );
      rail.sortRail(RailOrderType.DATE, RailOrderSort.ASCENDING);
      return rail;
    }
    return null;
  },
  /**
   * Filters a raw event list to those overlapping the timeline window, then
   * deduplicates by start time.
   */
  filterRailEvents(
    events: EpgEvent[],
    beginTime: number,
    endTime: number
  ): EpgEvent[] {
    if (!events?.length) return [];

    // Filter events within timeline range
    const inRange = events.filter((item) => {
      if (!item.date) return false;
      if (item.date?.start >= beginTime && item.date?.start < endTime) {
        return true;
      }
      if (item.date?.end <= endTime && item.date?.end > beginTime) {
        return true;
      }
      if (item.date.start <= beginTime && item.date.end >= endTime) {
        return true;
      }
      return false;
    });

    // Deduplicate by start time, keeping the first occurrence.
    const deduped = inRange.filter(
      (e, i, arr) => arr.findIndex((x) => x.date?.start === e.date?.start) === i
    );

    // Sort ascending by start time.
    const sorted = [...deduped].sort(
      (a, b) => (a.date?.start ?? 0) - (b.date?.start ?? 0)
    );

    return sorted;
  },
  /**
   * Inserts mock gap events into any uncovered portion of the timeline window:
   * before the first event, between consecutive events, and after the last event.
   */
  fillRailGaps(
    events: EpgEvent[],
    mockedEvent: EpgEvent,
    beginTime: number,
    endTime: number
  ): EpgEvent[] {
    const result = [...events];

    // Fill initial gap
    const firstStart = result[0]?.date?.start;
    if (
      (mockedEvent && !result.length) ||
      (firstStart !== undefined && firstStart > beginTime)
    ) {
      const gapEvent = lodash.cloneDeep(mockedEvent);
      gapEvent.date = {
        start: beginTime,
        end: result.length ? result[0].date?.start : endTime,
      };
      result.unshift(gapEvent);
    }

    // Fill intermediate gaps
    for (let i = 0; i < result.length - 1; i++) {
      const currentEnd = result[i].date?.end;
      const nextStart = result[i + 1].date?.start;
      if (
        currentEnd !== undefined &&
        nextStart !== undefined &&
        currentEnd < nextStart
      ) {
        const gapEvent = lodash.cloneDeep(mockedEvent);
        gapEvent.date = { start: currentEnd, end: nextStart };
        result.splice(i + 1, 0, gapEvent);
        i++;
      }
    }

    // Fill end gap
    const lastEnd = result[result.length - 1]?.date?.end;
    if (lastEnd !== undefined && lastEnd < endTime) {
      const gapEvent = lodash.cloneDeep(mockedEvent);
      gapEvent.date = {
        start: result.length ? lastEnd : beginTime,
        end: endTime,
      };
      result.push(gapEvent);
    }

    return result;
  },
};

export default EpgUtils;
